import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { PushQuoteService } from '@zero/shared/ui-push';
import { CustomerStateFacade, StoreErrorHandler } from '@zero/shared/util-global-state';
import { Asset, CustomerBaseData, MultiExanteResponse, Quote } from '@zero/shared/util-models';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { MinimumQuantitySurchargeService } from '../application/minimum-quantity-surcharge/minimum-quantity-surcharge.service';
import { TradingTimesService } from '../application/trading-times/trading-times.service';
import { MultiOrderInstrument } from '../entities/multi-order-instrument';
import { MultiOrderNotice } from '../entities/multi-order-notice';
import { CalendarInfrastructure } from '../infrastructure/calendar.infrastructure';
import { MultiOrderInfrastructure } from '../infrastructure/multi-order.infrastructure';
import { TradeInfrastructure } from '../infrastructure/trade.infrastructure';
import { tradeActions, tradeApiActions } from './trade.actions';
import { tradeFeature } from './trade.reducers';

@Injectable({ providedIn: 'root' })
export class TradeEffects {
  readonly #actions = inject(Actions);
  readonly #store = inject(Store);
  readonly #calendarInfrastructure = inject(CalendarInfrastructure);
  readonly #storeErrorHandler = inject(StoreErrorHandler);
  readonly #multiOrderInfrastructure = inject(MultiOrderInfrastructure);
  readonly #tradingTimesService = inject(TradingTimesService);
  readonly #translateService = inject(TranslocoService);
  readonly #minimumQuantitySurchargeService = inject(MinimumQuantitySurchargeService);
  readonly #customerStateFacade = inject(CustomerStateFacade);
  readonly #pushQuoteService = inject(PushQuoteService);
  readonly #tradeInfrastructure = inject(TradeInfrastructure);

  get #cbd(): CustomerBaseData {
    return this.#customerStateFacade.customerBaseData;
  }

  public loadHolidays$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.loadHolidays),
      concatLatestFrom(() => this.#store.select(tradeFeature.selectLoadingState)),
      filter(([, loadingState]) => loadingState === 'INITIAL'),
      map(() => tradeApiActions.loadHolidaysFromApi()),
    );
  });

  public loadHolidaysFromApi$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeApiActions.loadHolidaysFromApi),
      switchMap(() =>
        this.#calendarInfrastructure.getHolidays().pipe(
          map((holidays) => tradeApiActions.loadHolidaysSuccess({ holidays })),
          catchError((error) => of(tradeApiActions.loadHolidaysFailure(error))),
        ),
      ),
    );
  });

  public loadTradingDayInfo$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.loadTradingDayInfo),
      switchMap(({ isin }) =>
        this.#calendarInfrastructure.getTradingDayInfo(isin).pipe(
          map((tradingDayInfo) => tradeApiActions.loadTradingDayInfoSuccess({ tradingDayInfo })),
          catchError((error) => of(tradeApiActions.loadTradingDayInfoFailure(error))),
        ),
      ),
    );
  });

  public loadMultiOrderConsent$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.loadMultiOrderConsent),
      switchMap(() =>
        this.#multiOrderInfrastructure.getMultiOrderConsent().pipe(
          map((shouldDisplayConsent) => tradeApiActions.loadMultiOrderConsentSuccess({ shouldDisplayConsent })),
          catchError((error) => of(tradeApiActions.loadMultiOrderConsentFailure(error))),
        ),
      ),
    );
  });

  public confirmMultiOrderConsent$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.confirmMultiOrderConsent),
      switchMap(() =>
        this.#multiOrderInfrastructure.confirmMultiOrderConsent().pipe(
          map(() => tradeApiActions.confirmMultiOrderConsentSuccess()),
          catchError((error) => of(tradeApiActions.confirmMultiOrderConsentFailure(error))),
        ),
      ),
    );
  });

  public loadBuyingPower$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.loadBuyingPower),
      switchMap(() =>
        this.#multiOrderInfrastructure.loadBuyingPower().pipe(
          map((buyingPower) => tradeApiActions.loadBuyingPowerSuccess({ buyingPower })),
          catchError((error) => of(tradeApiActions.loadHolidaysFailure(error))),
        ),
      ),
    );
  });

  public validateMultiOrder = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.validateMultiOrder),
      concatLatestFrom(() => this.#store.select(tradeFeature.selectMultiOrderInstruments)),
      switchMap(([, multiOrderInstruments]) =>
        this.#multiOrderInfrastructure.validateMultiOrder(multiOrderInstruments).pipe(
          map(() => tradeApiActions.validateMultiOrderSuccess()),
          catchError((error) => of(tradeApiActions.validateMultiOrderFailure(error))),
        ),
      ),
    );
  });

  public sendMultiOrder$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.sendMultiOrder),
      concatLatestFrom(() => this.#store.select(tradeFeature.selectMultiOrderInstruments)),
      switchMap(([, multiOrderInstruments]) =>
        this.#multiOrderInfrastructure.createMultiOrder(multiOrderInstruments).pipe(
          map(() => tradeApiActions.sendMultiOrderSuccess()),
          catchError((error) => of(tradeApiActions.sendMultiOrderFailure(error))),
        ),
      ),
    );
  });

  public addMultiOrderInstruments$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.addMultiOrderInstruments),
      switchMap(({ instruments }) =>
        this.#multiOrderInfrastructure.loadAssets(instruments.map((i) => i.isin)).pipe(
          map((assets: Asset[]) =>
            assets?.length
              ? tradeApiActions.addMultiOrderInstrumentsSuccess({
                  instruments: this.#addAssets(instruments, assets),
                })
              : tradeApiActions.addMultiOrderInstrumentsFailure({
                  error: new HttpErrorResponse({ status: 404, statusText: 'No Assets Found' }),
                }),
          ),
          catchError((error) => of(tradeApiActions.addMultiOrderInstrumentsFailure(error))),
        ),
      ),
    );
  });

  public addMultiOrderInstrument$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.addMultiOrderInstrument),
      concatLatestFrom(() => this.#store.select(tradeFeature.selectLastAddedInstrument)),
      map(([, instrument]) => {
        const errorOrNoticeAction = this.#getErrorOrNoticeAction(instrument);
        return errorOrNoticeAction || tradeActions.setMultiOrderInstrumentError({ instrument, error: null });
      }),
    );
  });

  public subscribeToPushQuote$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.addMultiOrderInstrument),
      switchMap(({ instrument }) => {
        const { isin } = instrument.asset;
        return this.#pushQuoteService.subscribe(isin).pipe(map((quote) => tradeActions.updateQuote({ quote, isin })));
      }),
    );
  });

  public subscribeToPushQuotes$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeApiActions.addMultiOrderInstrumentsSuccess),
      switchMap(({ instruments }) => {
        const isins = instruments.map((i) => i.asset?.isin);
        return this.#pushQuoteService
          .subscribeMultiBuffered(isins)
          .pipe(map((quotes: Record<string, Quote>) => tradeActions.updateQuotes({ quotes })));
      }),
    );
  });

  public updateMinimumQuantitySurcharge$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.updateQuantity),
      concatLatestFrom(() => this.#store.select(tradeFeature.selectLastChangedInstrument)),
      map(([, instrument]) => {
        const { qty, asset, estimatedSum } = instrument;
        if (!qty) {
          return tradeActions.setMinimumQuantitySurcharge({ instrument, value: 0 });
        }
        const minimumQuantitySurcharge = this.#minimumQuantitySurchargeService.getMinimumQuantitySurcharge(estimatedSum, qty, asset);
        return tradeActions.setMinimumQuantitySurcharge({ instrument, value: minimumQuantitySurcharge.value });
      }),
    );
  });

  public updateQuantity$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.updateQuantity),
      concatLatestFrom(() => this.#store.select(tradeFeature.selectLastChangedInstrument)),

      map(([, instrument]) => {
        const { direction, qty, availableQty } = instrument;
        const errorPrefix = `trade.multi-order.create.${direction}.error`;
        const errorOrNoticeAction = this.#getErrorOrNoticeAction(instrument);
        if (errorOrNoticeAction) {
          return errorOrNoticeAction;
        }

        if (!qty) {
          return tradeActions.setMultiOrderInstrumentError({ instrument, error: `${errorPrefix}.quantity-required` });
        }

        if (qty < 1) {
          return tradeActions.setMultiOrderInstrumentError({ instrument, error: `${errorPrefix}.min` });
        }

        if (direction === 'sell' && qty > availableQty) {
          return tradeActions.setMultiOrderInstrumentError({ instrument, error: `${errorPrefix}.available-qty` });
        }

        if (!Number.isInteger(qty)) {
          return tradeActions.setMultiOrderInstrumentError({ instrument, error: `${errorPrefix}.fractions` });
        }

        return tradeActions.setMultiOrderInstrumentError({ instrument, error: null });
      }),
    );
  });

  public removeMultiOrderInstrument$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(tradeActions.removeMultiOrderInstrument),
        switchMap(({ instrument }) => of(this.#pushQuoteService.unsubscribe(instrument.asset.isin))),
      );
    },
    { dispatch: false },
  );

  public clearMultiOrder$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(tradeActions.clearMultiOrder),
        concatLatestFrom(() => this.#store.select(tradeFeature.selectMultiOrderInstruments)),
        switchMap(([, multiOrderInstruments]) =>
          of(this.#pushQuoteService.unsubscribeMulti(multiOrderInstruments.map((i) => i.isin))).pipe(
            map(() => tradeActions.removeMultiOrderInstruments()),
          ),
        ),
      );
    },
    { dispatch: false },
  );

  public loadMultiExante$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.loadExante),
      concatLatestFrom(() => this.#store.select(tradeFeature.selectMultiOrderInstruments)),
      switchMap(([, multiOrderInstruments]) =>
        this.#multiOrderInfrastructure.loadExante(multiOrderInstruments.map((i) => i.isin)).pipe(
          map((multiExante: MultiExanteResponse) => tradeApiActions.loadExanteSuccess({ multiExante })),
          catchError((error) => of(tradeApiActions.loadExanteFailure(error))),
        ),
      ),
    );
  });

  public verifyOrder$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.verifyOrder),
      switchMap(({ orderData }) =>
        this.#tradeInfrastructure.verifyOrder(orderData).pipe(
          map(() => tradeApiActions.verifyOrderSuccess()),
          catchError((error: HttpErrorResponse) => of(tradeApiActions.verifyOrderFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(tradeApiActions.verifyOrderFailure({ error }))),
    );
  });

  public validateCombinedOrder$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.validateCombinedOrder),
      switchMap(({ combinedOrder }) =>
        this.#tradeInfrastructure.validateCombinedOrder(combinedOrder).pipe(
          map((orders) => tradeApiActions.validateCombinedOrderSuccess({ orders })),
          catchError((error: HttpErrorResponse) => of(tradeApiActions.validateCombinedOrderFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(tradeApiActions.validateCombinedOrderFailure({ error }))),
    );
  });

  public placeOrder$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.placeOrder),
      switchMap(({ orderRequest }) =>
        this.#tradeInfrastructure.placeOrder(orderRequest).pipe(
          map(() => tradeApiActions.placeOrderSuccess()),
          catchError((error: HttpErrorResponse) => of(tradeApiActions.placeOrderFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(tradeApiActions.placeOrderFailure({ error }))),
    );
  });

  public placeCombinedOrder$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.placeCombinedOrder),
      switchMap(({ combinedOrderRequest }) =>
        this.#tradeInfrastructure.placeCombinedOrder(combinedOrderRequest).pipe(
          map(() => tradeApiActions.placeCombinedOrderSuccess()),
          catchError((error: HttpErrorResponse) => of(tradeApiActions.placeCombinedOrderFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(tradeApiActions.placeCombinedOrderFailure({ error }))),
    );
  });

  public loadKnowledgeAndExperienceForm$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.loadKnowledgeAndExperienceForm),
      switchMap(({ isins }) =>
        this.#tradeInfrastructure.loadKnowledgeAndExperienceForm(isins).pipe(
          map((response) => tradeApiActions.loadKnowledgeAndExperienceFormSuccess({ response })),
          catchError((error: HttpErrorResponse) => of(tradeApiActions.loadKnowledgeAndExperienceFormFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(tradeApiActions.loadKnowledgeAndExperienceFormFailure({ error }))),
    );
  });

  public submitKnowledgeAndExperienceForm$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(tradeActions.submitKnowledgeAndExperienceForm),
      switchMap(({ userExperienceData }) =>
        this.#tradeInfrastructure.submitKnowledgeAndExperienceForm(userExperienceData).pipe(
          map(() => tradeApiActions.submitKnowledgeAndExperienceFormSuccess()),
          catchError((error: HttpErrorResponse) => of(tradeApiActions.submitKnowledgeAndExperienceFormFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(tradeApiActions.submitKnowledgeAndExperienceFormFailure({ error }))),
    );
  });

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(
          tradeApiActions.loadHolidaysFailure,
          tradeApiActions.validateMultiOrderFailure,
          tradeApiActions.sendMultiOrderFailure,
          tradeApiActions.loadExanteFailure,
          tradeApiActions.verifyOrderFailure,
          tradeApiActions.validateCombinedOrderFailure,
          tradeApiActions.placeOrderFailure,
          tradeApiActions.placeCombinedOrderFailure,
          tradeApiActions.loadTradingDayInfoFailure,
          tradeApiActions.loadKnowledgeAndExperienceFormFailure,
          tradeApiActions.submitKnowledgeAndExperienceFormFailure,
        ),
        map((error) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );

  #hasTradingHoursError = ({ asset, direction }: MultiOrderInstrument) =>
    asset?.isin && direction === 'buy' && !this.#tradingTimesService.isActiveTradingHours(new Date(), asset);

  #getTradingHoursError = ({ tradingHoursStartTime, tradingHoursStopTime }: Asset) =>
    this.#translateService.translate('trade.multi-order.create.buy.error.trading-hours', {
      tradingHours:
        tradingHoursStartTime && tradingHoursStopTime
          ? `${tradingHoursStartTime.split(':', 1)[0]}-${tradingHoursStopTime.split(':', 1)[0]}`
          : '',
    });

  #addAssets = (instruments: MultiOrderInstrument[], assets: Asset[]) =>
    instruments.map((i) => ({ ...i, asset: assets.find(({ isin }) => i.isin === isin) }));

  #hasNoFtg = (asset: Asset): boolean => asset.complex && !this.#cbd.ftgPresent;

  #getErrorOrNoticeAction = (instrument: MultiOrderInstrument) => {
    const { asset, direction } = instrument;
    const isBuy = direction === 'buy';
    if (isBuy && this.#hasNoFtg(asset)) {
      const notice = this.#cbd?.fzContractActiveDate ? MultiOrderNotice.NO_FTG_BUT_ALLOWED_TO_ACTIVATE : MultiOrderNotice.NO_FTG;
      return tradeActions.setMultiOrderInstrumentNotice({
        instrument,
        notice,
      });
    }

    if (this.#hasTradingHoursError(instrument)) {
      return tradeActions.setMultiOrderInstrumentError({
        instrument,
        error: this.#getTradingHoursError(asset),
      });
    }

    if (!isBuy) {
      return null;
    }

    if (asset.leverage) {
      if (this.#cbd.orderCountLeveragedDerivatives < 10) {
        return tradeActions.setMultiOrderInstrumentNotice({
          instrument,
          notice: MultiOrderNotice.NO_LEVERAGED_DERIVATIVES_EXPERIENCE,
        });
      }
    } else if (asset.complex) {
      if (this.#cbd.orderCountNonLeveragedDerivatives < 10) {
        return tradeActions.setMultiOrderInstrumentNotice({
          instrument,
          notice: MultiOrderNotice.NO_NON_LEVERAGED_DERIVATIVES_EXPERIENCE,
        });
      }
    }

    return null;
  };
}
